import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ColorType, createChart, Rectangle } from 'lightweight-charts';
import { useMediaQuery, useTheme, Box, Grid, Typography, Container, Paper, TableContainer, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, IconButton, CircularProgress, keyframes } from '@mui/material';
import DataTable from "./table";
import BasicTable from "./table";
import EnhancedTable from "./table";
import { __fetching_data__ } from '../../components/api.js';
import { useState, useRef } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { tokens } from "../../theme";
import FontLoader from "../../components/FontLoader";

import { useIsMobile, useIsPortraitTablet, useIsTablet } from '../../components/hooks';

import './index.css';

const base_api_url =
    process.env.NODE_ENV === "production"
        ? window.location.protocol === "https:"
            ? process.env.REACT_APP_API_HTTPS_URL
            : process.env.REACT_APP_API_HTTP_URL
        : "/application";



const EachTable = ({ RegionData, collapsedTables, toggleTable, onClickTable, backgroundColors }) => {

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const blinkAnimation = keyframes`
  0% { background-color: transparent; }
  100% { background-color: green; }
`;

    const isMobile = useIsMobile();
    const isTablet = useIsTablet();
    const isPTablet = useIsPortraitTablet();

    const [selectedRow, setSelectedRow] = useState({});
    const [lastSelectedRegion, setLastSelectedRegion] = useState(null);

    const handleKeyPress = (event, items, region) => {

        setSelectedRow((prev) => {
            const currentIndex = prev[region] ?? -1;
            let newIndex = currentIndex;

            if (event.key === 'ArrowDown') {
                newIndex = currentIndex + 1 < items.length ? currentIndex + 1 : currentIndex;
            } else if (event.key === 'ArrowUp') {
                newIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : -1;
            } else if (event.key === 'Enter' && newIndex !== -1) {
                onClickTable(items[newIndex][0]);
            } else if (event.key === 'Escape') {
                return {};
            }

            if (newIndex !== currentIndex && newIndex !== -1) {
                onClickTable(items[newIndex][0]);
            }

            return newIndex === -1 ? {} : { [region]: newIndex };
        });

        setLastSelectedRegion(region);
    };

    const handleBlur = () => {
        setSelectedRow({});
        setLastSelectedRegion(null);
    };

    return (
        <Box className="dr-content" sx={{ display: 'flex', flexDirection: 'column', maxWidth: isMobile ? '100%' : '250px' }} >
            {Object.entries(RegionData).map(([region, items]) => (
                <Box key={region} className='dr-n-table' style={{ borderRadius: '10px' }} >
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        backgroundColor: '#004080',
                        borderRadius: '10px 10px 0 0',
                        justifyContent: 'space-between',
                    }}>
                        <Typography variant="h5" style={{
                            textAlign: 'center',
                            display: 'flex',
                            marginLeft: '30px',
                            alignItems: 'center',
                            color: theme.palette.text.sidebar2
                        }}>
                            {region}{" "}
                        </Typography>
                        <IconButton
                            onClick={() => toggleTable(region)}
                            style={{
                                color: theme.palette.text.sidebar2,
                                cursor: "pointer"
                            }}
                        >
                            {collapsedTables[region] ? (
                                <ExpandMoreIcon />
                            ) : (
                                <ExpandLessIcon />
                            )}
                        </IconButton>
                    </Box>

                    {!collapsedTables[region] && (
                        <TableContainer
                            tabIndex={0}
                            onKeyDown={(e) => handleKeyPress(e, items, region)}
                            onBlur={handleBlur} sx={{
                                outline: "none",
                                color: theme.palette.text.primary,
                                padding: '0 5px 5px 5px',
                                "& *": { color: theme.palette.text.primary },
                                backgroundColor: theme.palette.background.default,
                                borderRadius: '0 0 10px 10px',
                                boxShadow: 'rgba(0,0,0,0.1) 0px 2px 4px',
                            }}>
                            <Table sx={{ fontFamily: `'Lato, Sans-serif',` }} >
                                <TableHead sx={{
                                    fontFamily: `"Lato, Sans-serif,", sans-serif`,
                                    color: theme.palette.text.primary,
                                    backgroundColor: theme.palette.background.sidebar
                                }} >
                                    <TableRow>
                                        <TableCell sx={{ padding: '3px', fontWeight: 'bold', fontSize: '13px' }}>
                                            DR/DRx
                                        </TableCell>
                                        <TableCell sx={{ padding: '3px', fontWeight: 'bold', fontSize: '13px' }}>
                                            TradingView
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.map((item, index) => (
                                        <TableRow
                                            key={item[0]}
                                            onClick={() => {
                                                if (selectedRow[region] === index) {
                                                    setSelectedRow({});
                                                    setLastSelectedRegion(null);
                                                } else {
                                                    setSelectedRow({ [region]: index });
                                                    setLastSelectedRegion(region);
                                                    onClickTable(item[0]);
                                                }
                                            }}
                                            sx={{
                                                cursor: 'pointer',
                                                backgroundColor:
                                                    backgroundColors?.[item[0]] === "green"
                                                        ? "green"
                                                        : lastSelectedRegion === region && selectedRow[region] === index ? theme.palette.background.default :
                                                            index % 2 === 0 ? theme.palette.table.odd : theme.palette.table.even,
                                                animation:
                                                    backgroundColors[item[0]] === "green"
                                                        ? `${blinkAnimation} 1s infinite alternate`
                                                        : "none",
                                                fontFamily: `"Mitr", "Open Sans", "Source Sans Pro", sans-serif`,
                                                color: theme.palette.text.primary,
                                                "&:hover": {
                                                    backgroundColor: theme.palette.background.sidebar
                                                }
                                            }}
                                        >
                                            <TableCell sx={{ padding: '1px ', fontSize: '13px', color: theme.palette.text.primary, borderBottom: '0' }}>
                                                {item[1]}
                                            </TableCell>
                                            <TableCell sx={{ padding: '1px', fontSize: '13px', color: theme.palette.text.primary, borderBottom: '0' }}>
                                                <Tooltip title={item[4]}>
                                                    <Box>{item[2]}</Box>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>))}
        </Box>
    );
}

function DRPage_v4() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const csrfToken = useSelector((state) => state.csrfToken);
    const [symbol, setSymbol] = useState([])
    let [data, setData] = useState([])
    const [abc, setAbc] = useState(0)
    const [isLoad, setLoad] = useState(false)

    const [backgroundColors, setBackgroundColors] = useState([]);

    const [collapsedTables, setCollapsedTables] = useState({});

    const isMobile = useIsMobile();
    const isTablet = useIsTablet();
    const isPTablet = useIsPortraitTablet();

    // ✅ Each entry: { chart, resizeObserver, resizeHandler, container }
    const chartInstancesRef = useRef([]);
    // ✅ ref สำหรับ chart-div ที่ React ไม่ดูแล children
    const chartDivRef = useRef(null);

    const toggleTable = (region) => {
        setCollapsedTables((prev) => ({
            ...prev,
            [region]: !prev[region],
        }));
    };

    useEffect(() => {
        callAPI()
    }, [])

    useEffect(() => {
        if (!isMobile || !isTablet) {
            setCollapsedTables({});
        }
    }, [isMobile, isTablet]);

    // ✅ Cleanup เมื่อ unmount
    useEffect(() => {
        return () => {
            destroyAllCharts();
        };
    }, []);

    async function callAPI() {
        const response = await __fetching_data__({ url: `${base_api_url}/dr_list_symbol`, method: "GET", headers: { "xsrf-token": csrfToken } });
        if (response.status) {
            setData(response.data)
        }
    }

    // ✅ Disconnect observers/listeners BEFORE removing charts to prevent "Object is disposed" errors
    const destroyAllCharts = () => {
        chartInstancesRef.current.forEach(({ chart, resizeObserver, resizeHandler, container }) => {
            try { resizeObserver.unobserve(container); resizeObserver.disconnect(); } catch (e) {}
            try { window.removeEventListener("resize", resizeHandler); } catch (e) {}
            try { chart.remove(); } catch (e) {}
        });
        chartInstancesRef.current = [];
    };

    // ✅ ล้าง DOM ใน chartDivRef อย่างปลอดภัย ไม่ใช้ innerHTML หรือ .remove() โดยตรง
    const clearDiv = () => {
        destroyAllCharts();
        const el = chartDivRef.current;
        if (el) {
            el.replaceChildren(); // ✅ ปลอดภัยกับ React
        }
    };

    // ✅ รับ themeOptions เป็น argument เพราะ bgChart ไม่ใช่ React component จึงเรียก useTheme ไม่ได้
    function bgChart(name, data, chartName, lineColor, themeOptions) {
        const chartDiv = chartDivRef.current;
        if (!chartDiv) return [null, null];

        const filterContainer = document.createElement('div');
        filterContainer.setAttribute('id', name);
        filterContainer.style.width = "100%";
        filterContainer.style.borderRadius = '10px';
        filterContainer.style.overflow = 'hidden';
        filterContainer.style.height = (isMobile || isTablet) ? "200px" : "250px";
        chartDiv.appendChild(filterContainer);

        const chart = createChart(filterContainer, {
            width: filterContainer.clientWidth,
            height: filterContainer.clientHeight,
            timeScale: {
                rightOffset: data.length < 10 ? 0 : data.length < 100 ? 0.5 : 5
            }
        });

        // ✅ ใช้ themeOptions แทนการ hardcode สี — รองรับ dark/light mode
        chart.applyOptions({
            layout: {
                attributionLogo: false,
                background: {
                    type: 'solid',
                    color: themeOptions.background,
                },
                textColor: themeOptions.textColor,
            },
            watermark: {
                visible: true,
                fontSize: 24,
                horzAlign: 'center',
                vertAlign: 'center',
                color: 'rgba(255, 255, 255, 0.05)',
                text: `${chartName}`,
            },
            grid: {
                horzLines: { color: themeOptions.gridColor, visible: true },
                vertLines: { color: themeOptions.gridColor, visible: true },
            },
            crosshair: {
                horzLine: { color: themeOptions.crosshairColor },
                vertLine: { color: themeOptions.crosshairColor },
            },
        });

        // ✅ Named resize handler so it can be removed from window later
        const resizeHandler = () => {
            // Guard: only resize if this chart instance is still active
            const isActive = chartInstancesRef.current.some(c => c.chart === chart);
            if (!isActive) return;
            try {
                const containerWidth = filterContainer.clientWidth;
                const containerHeight = (isMobile || isPTablet) ? 200 : 250;
                chart.resize(containerWidth, containerHeight);
            } catch (e) {}
        };

        const resizeObserver = new ResizeObserver(resizeHandler);
        resizeObserver.observe(filterContainer);
        window.addEventListener("resize", resizeHandler);
        resizeHandler(); // initial size

        const lineSeries = chart.addLineSeries({ color: lineColor, priceLineVisible: false });
        lineSeries.setData(data);

        // ✅ Store everything needed for proper cleanup
        chartInstancesRef.current.push({ chart, resizeObserver, resizeHandler, container: filterContainer });

        return [chart, lineSeries];
    }

    const inList = (data, lst) => {
        for (let i = 0; i < lst.length; i++) {
            if (data == lst[i]['time']) {
                return i
            }
        }
        return undefined
    }

    const checkLatest10Data = async (data) => {
        const today = new Date();
        let backgroundStatus = {};

        const latest10 = data.slice(-10);
        const dt = data;

        await Promise.all(latest10.map(async (item) => {
            const id = item[0];

            try {
                const response = await __fetching_data__({
                    url: `${base_api_url}/dr_detail_symbol?id=${id}`,
                    method: "GET",
                    headers: { "xsrf-token": csrfToken }
                });

                if (!response || !response.data || !response.data.chart1 || response.data.chart1.length === 0) {
                    backgroundStatus[id] = "odd-even";
                    return;
                }

                let oldestDate = response.data.chart1
                    .map(entry => new Date(entry.time))
                    .sort((a, b) => a - b)[0];

                const today = new Date();
                const diffDays = (today - oldestDate) / (1000 * 60 * 60 * 24);

                backgroundStatus[id] = diffDays <= 7 ? "green" : "odd-even";

            } catch (error) {
                console.error(`Error fetching data for id ${id}:`, error);
                backgroundStatus[id] = "odd-even";
            }
        }));

        return backgroundStatus;
    };

    useEffect(() => {
        const fetchData = async () => {
            const result = await checkLatest10Data(data);
            setBackgroundColors(result);
        };
        fetchData();
    }, [data]);

    const onClickTable = async (id) => {
        setLoad(true)
        const response = await __fetching_data__({ url: `${base_api_url}/dr_detail_symbol?id=${id}`, method: "GET", headers: { "xsrf-token": csrfToken } });
        let data = []
        if (response.status) {
            for (let i = 0; i < response.data.chart3.length; i++) {
                const chartData = response.data.chart3[i]
                const dateTime = chartData['date'].includes(' ')
                    ? chartData['date'].split(' ')[0]
                    : chartData['date'].split('T')[0];
                let dateData;

                if (typeof chartData['date'] === 'string') {
                    dateData = dateTime.replaceAll('.', '-');
                } else if (chartData['date'] instanceof Date) {
                    const year = chartData['date'].getFullYear()
                    const month = String(chartData['date'].getMonth() + 1).padStart(2, '0')
                    const day = String(chartData['date'].getDate()).padStart(2, '0')
                    dateData = `${year}-${month}-${day}`
                } else {
                    continue;
                }

                let dataInList = inList(dateData, data)
                if (dataInList != undefined) {
                    data[dataInList]['value'] = Number(chartData['value'])
                } else {
                    data.push({ 'time': dateData, 'value': Number(chartData['value']) })
                }
            }

            // ✅ ล้าง chart เดิมอย่างถูกต้อง (disconnects observers/listeners first)
            clearDiv();

            function compare(a, b) {
                if (a.time < b.time) return -1;
                if (a.time > b.time) return 1;
                return 0;
            }
            data.sort(compare)
            response.data.chart1.sort(compare)
            response.data.chart2.sort(compare)

            const getCommonDates = (chart1, chart2, chart3) => {
                const dates1 = new Set(chart1.map(item => item.time));
                const dates3 = new Set(chart3.map(item => item.time));

                if (!chart2 || chart2.length === 0) {
                    return [...dates1].filter(date => dates3.has(date));
                } else {
                    const dates2 = new Set(chart2.map(item => item.time));
                    return [...dates1].filter(date => dates2.has(date) && dates3.has(date));
                }
            }

            const filterDataByCommonDates = (data, commonDates) => {
                return data.filter(item => commonDates.includes(item.time));
            }

            const isChart2Empty = !response.data.chart2 || response.data.chart2.length === 0;
            const commonDates = getCommonDates(response.data.chart1, response.data.chart2, data);

            response.data.chart1 = filterDataByCommonDates(response.data.chart1, commonDates);
            if (!isChart2Empty) {
                response.data.chart2 = filterDataByCommonDates(response.data.chart2, commonDates);
            }
            data = filterDataByCommonDates(data, commonDates);

            const themeOptions = {
                background: '#0d1421',
                textColor: '#94a3b8',
                gridColor: 'rgba(26, 39, 68, 0.4)',   // ← โปร่งขึ้น ปรับ 0.4 ได้
                crosshairColor: '#3b82f6',
            };

            var [chart1, mainSeries1] = bgChart('chart1', response.data.chart1, response.data.name.chart1, colors.blueChart[50], themeOptions)
            chart1.timeScale().fitContent();
            var [chart2, mainSeries2] = bgChart('chart2', response.data.chart2, response.data.name.chart2, colors.redChart[300], themeOptions)
            chart2.timeScale().fitContent();
            var [chart3, mainSeries3] = bgChart('chart3', data, response.data.name.chart3, colors.greenChart[50], themeOptions)
            chart3.timeScale().fitContent();

            chart1.timeScale().subscribeVisibleLogicalRangeChange(timeRange => {
                chart1.timeScale().setVisibleLogicalRange(timeRange);
                chart2.timeScale().setVisibleLogicalRange(timeRange);
                chart3.timeScale().setVisibleLogicalRange(timeRange);
            });

            chart2.timeScale().subscribeVisibleLogicalRangeChange(timeRange => {
                chart1.timeScale().setVisibleLogicalRange(timeRange);
                chart2.timeScale().setVisibleLogicalRange(timeRange);
                chart3.timeScale().setVisibleLogicalRange(timeRange);
            });

            chart3.timeScale().subscribeVisibleLogicalRangeChange(timeRange => {
                chart1.timeScale().setVisibleLogicalRange(timeRange);
                chart2.timeScale().setVisibleLogicalRange(timeRange);
                chart3.timeScale().setVisibleLogicalRange(timeRange);
            });

            function getCrosshairDataPoint(series, param) {
                if (!param.time) return null;
                const dataPoint = param.seriesData.get(series);
                return dataPoint || null;
            }

            function syncCrosshair(chart, series, dataPoint) {
                try {
                    if (dataPoint && dataPoint.value != null && dataPoint.time != null) {
                        chart.setCrosshairPosition(dataPoint.value, dataPoint.time, series);
                    } else {
                        chart.clearCrosshairPosition();
                    }
                } catch (error) {
                    console.error("Error in syncCrosshair: ", error);
                }
            }

            chart1.subscribeCrosshairMove(param => {
                const dataPoint = getCrosshairDataPoint(mainSeries1, param);
                if (dataPoint) {
                    syncCrosshair(chart2, mainSeries2, dataPoint);
                    syncCrosshair(chart3, mainSeries3, dataPoint);
                }
            });
            chart2.subscribeCrosshairMove(param => {
                const dataPoint = getCrosshairDataPoint(mainSeries2, param);
                syncCrosshair(chart1, mainSeries1, dataPoint);
                syncCrosshair(chart3, mainSeries3, dataPoint);
            });
            chart3.subscribeCrosshairMove(param => {
                const dataPoint = getCrosshairDataPoint(mainSeries3, param);
                syncCrosshair(chart1, mainSeries1, dataPoint);
                syncCrosshair(chart2, mainSeries2, dataPoint);
            });

            setLoad(false)
        }
    }

    const categorizeByStaticId = (data) => {
        return {
            "USA": data.filter((item) => item[2].includes("NYSE") || item[2].includes("NASDAQ")),
            "Europe": data.filter((item) => item[2].includes("MIL") || item[2].includes("EURONEXT") || item[2].includes("OMXCOP")),
            "ETC": data.filter((item) => [181, 241, 243].includes(item[0]))
        }
    };

    const categorizeByStaticId2 = (data) => {
        return {
            "Japan": data.filter((item) => item[2].includes("TSE")),
            "Singapore": data.filter((item) => item[2].includes("SGX") && item[0] !== 181),
            "Vietnam": data.filter((item) => item[2].includes("HOSE")),
        }
    };

    const categorizeByStaticId3 = (data) => {
        return {
            "China": data.filter((item) => item[2].includes("HKEX") && ![241, 243].includes(item[0])),
            "Taiwan": data.filter((item) => item[2].includes("TWSE")),
        }
    };

    const groupedDataLeft = categorizeByStaticId(data);
    const groupedDataRight = categorizeByStaticId2(data);
    const groupedDataRight2 = categorizeByStaticId3(data);

    return (<div>
        <Box className="main-content" sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            flexDirection: 'column',
            paddingTop: isMobile ? '0px' : '10px',
            paddingLeft: (isMobile || isPTablet) ? '0px' : '10px',
        }}>
            <Box sx={{ display: isMobile ? 'none' : 'flex', justifyContent: 'space-around' }}>
                <Box sx={{ height: '40px', backgroundColor: '#0d1421', borderRadius: '20px', display: 'flex', alignItems: 'center', padding: '5px 1vw 5px 1vw', gap: '10px' }}>
                    <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 'bold', fontFamily: 'Lato, Sans-serif' }}>
                        ราคาอ้างอิง:
                    </Typography>
                    <div style={{ height: '4px', width: '50px', backgroundColor: colors.blueChart[50] }} />
                </Box>
                <Box sx={{ height: '40px', backgroundColor: '#0d1421', borderRadius: '20px', display: 'flex', alignItems: 'center', padding: '5px 1vw 5px 1vw', gap: '10px' }}>
                    <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 'bold', fontFamily: 'Lato, Sans-serif' }}>
                        PE Ratio:
                    </Typography>
                    <div style={{ height: '4px', width: '50px', backgroundColor: colors.redChart[300] }} />
                </Box>
                <Box sx={{ height: '40px', backgroundColor: '#0d1421', borderRadius: '20px', display: 'flex', alignItems: 'center', padding: '5px 1vw 5px 1vw', gap: '10px' }}>
                    <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 'bold', fontFamily: 'Lato, Sans-serif' }}>
                        Last:
                    </Typography>
                    <div style={{ height: '4px', width: '50px', backgroundColor: colors.greenChart[50] }} />
                </Box>
            </Box>

            <Container maxWidth='xl' sx={{ display: 'flex', padding: ' 1vh 2vw 2vh 2vw' }}>
                <Paper sx={{
                    padding: '1vw',
                    display: 'flex',
                    width: '100%',
                    flexDirection: { xs: 'column', sm: 'column', md: 'row', lg: 'row', xl: 'row' },
                    alignItems: { xs: 'center', sm: 'center', md: 'normal', lg: 'normal', xl: 'normal' },
                    gap: (isMobile || isPTablet) ? '1vh' : 'normal',
                    justifyContent: 'space-around',
                    backgroundColor: '#0d1421',
                }}>

                    {(!isMobile && !isTablet) &&
                        <EachTable
                            RegionData={groupedDataLeft}
                            collapsedTables={collapsedTables}
                            toggleTable={toggleTable}
                            onClickTable={onClickTable}
                            backgroundColors={backgroundColors}
                        />
                    }

                    {(isTablet && !isPTablet) &&
                        <Box className="big-block" sx={{
                            borderRadius: '10px',
                            display: 'flex',
                            height: 'fit-content',
                            backgroundColor: '#0d1421',
                            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 2px 4px',
                            flexDirection: 'column',
                            gap: '10px',
                            maxHeight: '80vh',
                            overflowY: 'auto',
                            '::-webkit-scrollbar': { width: '5px', height: '5px' },
                            '::-webkit-scrollbar-thumb': { backgroundColor: '#888', borderRadius: '4px' },
                            '::-webkit-scrollbar-thumb:hover': { backgroundColor: '#555', },
                        }}>
                            <div className="big-block-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '10px 10px 0 0', backgroundColor: '#004080', margin: '0' }}>
                                <h3 style={{ margin: '0', padding: '3px', color: theme.palette.text.sidebar2, zIndex: 1111 }}>EU/USA</h3>
                            </div>
                            <Box className="right-block" sx={{ gap: '1vw', padding: '0 5px 0 5px', display: 'flex', flexDirection: (isTablet || isPTablet || isMobile) ? 'column' : 'unset' }}>
                                <EachTable
                                    RegionData={groupedDataLeft}
                                    collapsedTables={collapsedTables}
                                    toggleTable={toggleTable}
                                    onClickTable={onClickTable}
                                    backgroundColors={backgroundColors}
                                />
                            </Box>
                        </Box>
                    }

                    {isLoad && <div style={{ zIndex: 100, position: 'absolute', top: '30%', left: '60%' }}><CircularProgress sx={{ color: theme.palette.text.primary }} /></div>}

                    {/* ✅ chart-div: ใช้ ref แทน id และ React ไม่มี children ใน div นี้ */}
                    <Box
                        className="all-chart"
                        sx={{
                            width: { xs: '90%', sm: '90%', md: '50%', lg: '50%', xl: '60%' },
                            height: 'fit-content',
                            backgroundColor: '#0d1421',
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: '10px',
                            padding: '1vw',
                            gap: (isMobile || isTablet) ? '5px' : '2vh',
                            '& > div': { borderRadius: '10px' }
                        }}
                    >
                        {/* ✅ KEY: div นี้ React ไม่มี children → ปลอดภัย 100% */}
                        <div ref={chartDivRef} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: (isMobile || isTablet) ? '5px' : '2vh' }} />
                    </Box>

                    <Box className="all-contents" sx={{
                        maxHeight: (isMobile || isPTablet) ? '30vh' : '90vh',
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '1vw',
                    }}>
                        <Box className="big-block" sx={{
                            borderRadius: '10px',
                            display: 'flex',
                            height: 'fit-content',
                            backgroundColor: '#0d1421',
                            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 2px 4px',
                            flexDirection: 'column',
                            gap: '10px',
                            maxHeight: isMobile ? '30vh' : '86vh',
                            overflowY: 'auto',
                            '::-webkit-scrollbar': { width: '5px', height: '5px' },
                            '::-webkit-scrollbar-thumb': { backgroundColor: '#888', borderRadius: '4px' },
                            '::-webkit-scrollbar-thumb:hover': { backgroundColor: '#555' },
                        }}>
                            <div className="big-block-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '10px 10px 0 0', backgroundColor: '#004080', margin: '0' }}>
                                <h3 style={{ margin: '0', padding: '3px', color: theme.palette.text.sidebar2, zIndex: 1111 }}>Asia</h3>
                            </div>
                            <Box className="right-block" sx={{ gap: '1vw', padding: '0 5px 0 5px', display: 'flex', flexDirection: (isTablet || isPTablet || isMobile) ? 'column' : 'unset' }}>
                                <EachTable
                                    RegionData={groupedDataRight}
                                    collapsedTables={collapsedTables}
                                    toggleTable={toggleTable}
                                    onClickTable={onClickTable}
                                    backgroundColors={backgroundColors}
                                />
                                <EachTable
                                    RegionData={groupedDataRight2}
                                    collapsedTables={collapsedTables}
                                    toggleTable={toggleTable}
                                    onClickTable={onClickTable}
                                    backgroundColors={backgroundColors}
                                />
                            </Box>
                        </Box>

                        {(isPTablet || isMobile) && (
                            <Box className="right-content2" sx={{
                                gap: '10px',
                                borderRadius: '10px',
                                display: 'flex',
                                height: 'fit-content',
                                maxHeight: isMobile ? '30vh' : 'unset',
                                backgroundColor: '#0d1421',
                                boxShadow: 'rgba(0, 0, 0, 0.1) 0px 2px 4px',
                                flexDirection: 'column',
                                overflowY: 'auto',
                                '::-webkit-scrollbar': { width: '5px', height: '5px' },
                                '::-webkit-scrollbar-thumb': { backgroundColor: '#888', borderRadius: '4px' },
                                '::-webkit-scrollbar-thumb:hover': { backgroundColor: '#555' },
                            }}>
                                <>
                                    <div className="big-block-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '10px 10px 0 0', backgroundColor: '#004080', margin: '0' }}>
                                        <h3 style={{ margin: '0', padding: '3px', color: theme.palette.text.sidebar2, zIndex: 1111 }}>America / EU</h3>
                                    </div>
                                    <Box sx={{ padding: '0 5px 0 5px' }}>
                                        <EachTable
                                            RegionData={groupedDataLeft}
                                            collapsedTables={collapsedTables}
                                            toggleTable={toggleTable}
                                            onClickTable={onClickTable}
                                            backgroundColors={backgroundColors}
                                        />
                                    </Box>
                                </>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Container>
        </Box>
    </div>)
}

export default DRPage_v4